// Ativar 2FA (TOTP): palavra-passe → código QR + chave → primeiro código → códigos de recuperação.
// Usado no backoffice (obrigatório para staff) e na página de conta do site (opcional para jogadores).
import { authErrorKey } from "@ecliptica/api-client/errors";
import { useAuthClient, useMe, useRefreshMe } from "@ecliptica/api-client/react";
import { useTranslation } from "@ecliptica/i18n";
import { Alert, Button, Field, toast } from "@ecliptica/ui";
import QRCode from "qrcode";
import { useState, type FormEvent } from "react";

export function TwoFactorSetup({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation("common");
  const auth = useAuthClient();
  const refreshMe = useRefreshMe();
  const { data: me } = useMe();
  const [step, setStep] = useState<"password" | "scan" | "backup">("password");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState("");
  const [key, setKey] = useState("");
  const [backup, setBackup] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function start(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: err } = await auth.twoFactor.enable({ password });
    setBusy(false);
    if (err || !data || data.method !== "totp") return setError(authErrorKey(err));
    setKey(new URL(data.totpURI).searchParams.get("secret") ?? "");
    setQr(await QRCode.toDataURL(data.totpURI, { margin: 1, width: 220, color: { dark: "#0b0c22", light: "#ffffff" } }));
    setBackup(data.backupCodes);
    setStep("scan");
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await auth.twoFactor.verifyTotp({ code: code.replace(/\s/g, "") });
    setBusy(false);
    if (err) return setError(authErrorKey({ ...err, code: err.code ?? "INVALID_CODE" }));
    setStep("backup");
  }

  async function finish() {
    await refreshMe();
    toast.success(t("auth.setup.done"));
    onDone?.();
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-xl font-extrabold text-gold">{t("auth.setup.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("auth.setup.intro")}</p>
      </div>
      {step === "password" && (
        <form onSubmit={start} className="flex flex-col gap-4">
          <p className="text-sm">1. {t("auth.setup.step1")}</p>
          {/* Sem o nome de utilizador ao lado, o gestor de palavras-passe do browser não sabe que conta preencher. */}
          <input type="text" name="username" autoComplete="username" value={me?.user?.email ?? ""} readOnly hidden />
          <Field label={t("auth.password")} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <Alert variant="destructive">{t(error)}</Alert>}
          <Button type="submit" variant="primary" disabled={busy}>
            {t("auth.setup.start")}
          </Button>
        </form>
      )}
      {step === "scan" && (
        <form onSubmit={verify} className="flex flex-col gap-4">
          <p className="text-sm">2. {t("auth.setup.step2")}</p>
          <img src={qr} alt="QR" width={220} height={220} className="mx-auto rounded-md bg-white p-2" />
          <p className="break-all text-center font-mono text-xs text-muted-foreground">
            {t("auth.setup.key")}: {key}
          </p>
          <p className="text-sm">3. {t("auth.setup.step3")}</p>
          <Field
            label={t("auth.code")}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="[&_input]:text-center [&_input]:font-mono [&_input]:text-lg [&_input]:tracking-[0.4em]"
          />
          {error && <Alert variant="destructive">{t(error)}</Alert>}
          <Button type="submit" variant="primary" disabled={busy}>
            {t("auth.verify")}
          </Button>
        </form>
      )}
      {step === "backup" && (
        <div className="flex flex-col gap-4">
          <Alert variant="success">{t("auth.setup.done")}</Alert>
          <div>
            <h3 className="font-semibold text-gold">{t("auth.setup.backupTitle")}</h3>
            <p className="text-sm text-muted-foreground">{t("auth.setup.backupIntro")}</p>
          </div>
          <ul className="grid grid-cols-2 gap-1 rounded-md bg-muted p-3 font-mono text-sm">
            {backup.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigator.clipboard.writeText(backup.join("\n")).then(() => toast.success(t("auth.setup.copied")))}
            >
              {t("auth.setup.copy")}
            </Button>
            <Button type="button" variant="primary" onClick={() => void finish()}>
              {t("action.close")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
